import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { IconButton, Tooltip, Typography } from '@mui/material';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  CellContext,
  ColumnHelper,
  defineColumns,
  SortingState,
  TanstackTable,
  useServerPagination,
} from '@thingslog/ui-components';
import { QueryKeys } from '@thingslog/queries';
import { CommandState, CommandType, Device, DeviceCommandDto } from '@thingslog/repositories';
import Header from '../../../components/header';
import { ReduxState } from '../../../reducers';
import { commandsQueryClient } from '../../../clients/ReactQueryClients/ReactQueryClients';
import CommandStateChip from './components/CommandStateChip';
import CommandParametersCell from './components/CommandParametersCell';
import CommandsPageHeader from './components/CommandsPageHeader';
import SendCommandDialog from './components/SendCommandDialog';
import CancelCommandDialog from './components/CancelCommandDialog';
import useCommandParameterValueLabels from './utils/useCommandParameterValueLabels';
import {
  parseCommandDate,
  translateCommandState,
  translateCommandType,
} from './utils/commandLabels';

const AUTO_REFRESH_INTERVAL_MS = 30_000;
const DATE_FORMAT = 'dd/MM/yyyy HH:mm';
const TIME_FORMAT = 'HH:mm:ss';

const NO_DATE = '—';

const NO_SORTING: SortingState = [];

// Anything outside this set 500s the endpoint.
const SORTABLE_FIELDS: Record<string, string> = {
  id: 'id',
  commandType: 'commandType',
  commandState: 'commandState',
  creationDate: 'creationDate',
  sentDate: 'sentDate',
  executionDate: 'executionDate',
};

const toSortParam = (sorting: SortingState): string | undefined => {
  const entry = sorting[0];
  const field = entry && SORTABLE_FIELDS[entry.id];
  if (!field) return undefined;
  return `${field},${entry.desc ? 'desc' : 'asc'}`;
};

const formatDate = (value: string | null): string => {
  const date = parseCommandDate(value);
  return date ? format(date, DATE_FORMAT) : NO_DATE;
};

const CommandsPage: FC = () => {
  const { t } = useTranslation();
  const { deviceNumber } = useParams<{ deviceNumber: string }>();
  const selectedDevice = useSelector(
    (state: ReduxState): Device | null => state.dev.selectedDevice
  );
  const queryClient = useQueryClient();

  const [isAutoRefreshOn, setIsAutoRefreshOn] = useState<boolean>(true);
  const [sorting, setSorting] = useState<SortingState>(NO_SORTING);
  const [isSendOpen, setIsSendOpen] = useState<boolean>(false);
  const [commandToCancel, setCommandToCancel] = useState<DeviceCommandDto | null>(null);
  const [isManualRefreshing, setIsManualRefreshing] = useState<boolean>(false);

  const { useCommands } = useMemo(() => commandsQueryClient, []);
  const { page, size, isReady, resetPage, tableProps } = useServerPagination({
    initialPageSize: 25,
    pageSizeOptions: [10, 25, 50],
  });

  const commandsQuery = useCommands(deviceNumber ?? '', page, size, toSortParam(sorting), {
    enabled: Boolean(deviceNumber) && isReady,
    keepPreviousData: true,
    refetchInterval: isAutoRefreshOn ? AUTO_REFRESH_INTERVAL_MS : false,
    refetchIntervalInBackground: isAutoRefreshOn,
    refetchOnWindowFocus: isAutoRefreshOn,
  });

  // Not isLoading: keepPreviousData means page/size/sort changes never enter it.
  const isTableLoading =
    commandsQuery.isLoading || commandsQuery.isPreviousData || isManualRefreshing;

  const isUpdating = commandsQuery.isFetching;

  const rows = useMemo(
    (): DeviceCommandDto[] => commandsQuery.data?.content ?? [],
    [commandsQuery.data]
  );

  const visibleCommandTypes = useMemo(
    (): CommandType[] => rows.map((row: DeviceCommandDto): CommandType => row.commandType),
    [rows]
  );

  const { labels: valueLabels, shared: sharedValueLabels } = useCommandParameterValueLabels(
    deviceNumber ?? '',
    visibleCommandTypes
  );

  const refresh = useCallback((): void => {
    setIsManualRefreshing(true);
    commandsQuery.refetch().finally((): void => setIsManualRefreshing(false));
  }, [commandsQuery]);

  const invalidate = useCallback((): void => {
    queryClient.invalidateQueries([QueryKeys.GetDeviceCommands, deviceNumber]);
  }, [queryClient, deviceNumber]);

  const onSent = useCallback((): void => {
    resetPage();
    invalidate();
  }, [resetPage, invalidate]);

  const freshness = useMemo((): ReactNode => {
    if (!commandsQuery.dataUpdatedAt) return null;
    return t('commands_last_updated', {
      time: format(new Date(commandsQuery.dataUpdatedAt), TIME_FORMAT),
    });
  }, [commandsQuery.dataUpdatedAt, t]);

  const columns = useMemo(
    () =>
      defineColumns<DeviceCommandDto>((column: ColumnHelper<DeviceCommandDto>) => {
        const header = (label: string): ReactNode => (
          <Typography variant="body2" className="font-semibold">
            {label}
          </Typography>
        );

        return [
          column.accessor('id', {
            header: (): ReactNode => header(t('commands_command_id')),
            meta: { label: t('commands_command_id'), minWidth: 90 },
            cell: (info: CellContext<DeviceCommandDto, number>): ReactNode => (
              <Typography variant="body2" color="text.secondary">
                {info.getValue()}
              </Typography>
            ),
          }),
          // Translate in cell, not accessor: accessor results are cached per row.
          column.accessor('commandType', {
            header: (): ReactNode => header(t('commands_type')),
            meta: { label: t('commands_type'), minWidth: 170 },
            cell: (info: CellContext<DeviceCommandDto, CommandType>): ReactNode => (
              <Typography variant="body2" className="font-semibold">
                {translateCommandType(t, info.getValue())}
              </Typography>
            ),
          }),
          column.accessor('commandState', {
            header: (): ReactNode => header(t('commands_status')),
            meta: { label: t('commands_status'), minWidth: 120 },
            cell: (info: CellContext<DeviceCommandDto, CommandState>): ReactNode => (
              <CommandStateChip
                state={info.getValue()}
                label={translateCommandState(t, info.getValue())}
              />
            ),
          }),
          column.display({
            id: 'commandParameters',
            enableSorting: false,
            header: (): ReactNode => header(t('commands_parameters')),
            meta: { label: t('commands_parameters'), grow: true, minWidth: 240 },
            cell: (info: CellContext<DeviceCommandDto, unknown>): ReactNode => (
              <CommandParametersCell
                commandType={info.row.original.commandType}
                parameters={info.row.original.commandParameters}
                valueLabels={valueLabels}
                sharedValueLabels={sharedValueLabels}
              />
            ),
          }),
          column.accessor('creationDate', {
            header: (): ReactNode => header(t('commands_created')),
            meta: { label: t('commands_created'), minWidth: 140 },
            cell: (info: CellContext<DeviceCommandDto, string>): ReactNode => (
              <Typography variant="body2" className="whitespace-nowrap">
                {formatDate(info.getValue())}
              </Typography>
            ),
          }),
          column.accessor('sentDate', {
            header: (): ReactNode => header(t('commands_sent')),
            meta: { label: t('commands_sent'), minWidth: 140 },
            cell: (info: CellContext<DeviceCommandDto, string | null>): ReactNode => (
              <Typography variant="body2" color="text.secondary" className="whitespace-nowrap">
                {formatDate(info.getValue())}
              </Typography>
            ),
          }),
          column.accessor('executionDate', {
            header: (): ReactNode => header(t('commands_executed')),
            meta: { label: t('commands_executed'), minWidth: 140 },
            cell: (info: CellContext<DeviceCommandDto, string | null>): ReactNode => (
              <Typography variant="body2" className="whitespace-nowrap">
                {formatDate(info.getValue())}
              </Typography>
            ),
          }),
          column.display({
            id: 'actions',
            enableSorting: false,
            header: '',
            meta: { align: 'right', minWidth: 64 },
            cell: (info: CellContext<DeviceCommandDto, unknown>): ReactNode => {
              if (info.row.original.commandState !== 'PENDING') return null;

              return (
                <Tooltip title={t('commands_cancel_command')} arrow>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={t('commands_cancel_command')}
                    onClick={(): void => setCommandToCancel(info.row.original)}
                    className="min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0"
                  >
                    <CancelScheduleSendIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              );
            },
          }),
        ];
      }),
    [t, valueLabels, sharedValueLabels]
  );

  if (!deviceNumber) {
    return (
      <Header>
        <div className="p-4" role="alert">
          <Typography variant="h5" component="h1" className="mb-2 font-semibold">
            {t('commands_header')}
          </Typography>
          <Typography color="error">{t('commands_error_missing_device_number')}</Typography>
        </div>
      </Header>
    );
  }

  return (
    <Header fullHeightContent>
      {/* Row flex parent: w-full or it pins left, min-w-0 or it blows past the viewport. */}
      <div className="mx-auto flex h-full min-h-0 w-full min-w-0 max-w-[2560px] flex-col gap-4 px-4 py-4 md:px-6">
        <CommandsPageHeader
          deviceNumber={deviceNumber}
          device={selectedDevice}
          isAutoRefreshOn={isAutoRefreshOn}
          onAutoRefreshChange={setIsAutoRefreshOn}
          onRefresh={refresh}
          onSend={(): void => setIsSendOpen(true)}
          isRefreshing={isManualRefreshing}
          isUpdating={isUpdating}
          freshness={freshness}
        />

        <TanstackTable<DeviceCommandDto>
          mode="server"
          className="w-full min-h-0 flex-1"
          data={rows}
          rowCount={commandsQuery.data?.totalElements ?? 0}
          columns={columns}
          getRowId={(row: DeviceCommandDto): string => String(row.id)}
          isLoading={isTableLoading}
          density="standard"
          showToolbar={false}
          state={{ sorting }}
          onSortingChange={setSorting}
          enableColumnFilters={false}
          enableGlobalFilter={false}
          emptyMessage={t('commands_no_commands')}
          error={
            commandsQuery.isError && rows.length === 0 ? t('commands_error_list_failed') : undefined
          }
          {...tableProps}
        />
      </div>

      <SendCommandDialog
        open={isSendOpen}
        deviceNumber={deviceNumber}
        onClose={(): void => setIsSendOpen(false)}
        onSent={onSent}
      />

      <CancelCommandDialog
        command={commandToCancel}
        deviceNumber={deviceNumber}
        onClose={(): void => setCommandToCancel(null)}
        onCancelled={invalidate}
      />
    </Header>
  );
};

export default CommandsPage;
